import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from '@cortex/web-design-system/components/ui/Modal';
import { Button } from '@cortex/web-design-system/components/ui/Button';
import { useKpiBugDetectionDays } from '../../../hooks/index.js';
import { extractDateString } from '../../../lib/date-utils.js';
import { escapeCsvValue, downloadCsv } from '../../../lib/csv.js';
import type { KpiTeam } from '../../../types/api.js';

interface BugDetectionDaysModalProps {
  open: boolean;
  onClose: () => void;
  team: KpiTeam;
  teamDisplayName: string;
  /** クリックされた日付（この日を終点とする過去30日窓の詳細を表示） */
  date: string;
}

/**
 * YYYY-MM-DD 2 日付の差分日数（a - b）を返す。パース不能なら null。
 * @graph-connects none
 */
function diffDays(a: string | null, b: string | null): number | null {
  if (!a || !b) return null;
  const [ay, am, ad] = a.split('-').map(Number);
  const [by, bm, bd] = b.split('-').map(Number);
  if (!ay || !am || !ad || !by || !bm || !bd) return null;
  const aTime = Date.UTC(ay, am - 1, ad);
  const bTime = Date.UTC(by, bm - 1, bd);
  return Math.round((aTime - bTime) / 86_400_000);
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business バグ検知日数分布チャートのドリルダウンモーダル。クリックされた日に発見された案件起因バグを一覧表示し、各バグの検知日数（発見日 − 起因案件リリース完了日）と平均・CSVダウンロードを提供する
 * @graph-connects useKpiBugDetectionDays [calls] 指定チーム・起票日窓のバグ検知日数詳細データを取得
 * @graph-connects extractDateString [calls] 日付文字列正規化ユーティリティ呼び出し
 */
export function BugDetectionDaysModal({
  open,
  onClose,
  team,
  teamDisplayName,
  date,
}: BugDetectionDaysModalProps) {
  const { t } = useTranslation();

  const displayDate = useMemo(() => extractDateString(date), [date]);

  // クリックした日に発見された（発見日 = その日の）バグのみを対象にする
  const params = useMemo(() => {
    if (!open || !displayDate) return null;
    const [year, month, day] = displayDate.split('-').map(Number);
    if (!year || !month || !day) return null;
    return {
      team,
      startDate: displayDate,
      endDate: displayDate,
      limit: 500,
    };
  }, [open, displayDate, team]);

  const { data, isLoading } = useKpiBugDetectionDays(params);

  // 各バグに発見日（初回発生日、空なら起票日）と検知日数を付与
  const rows = useMemo(() => {
    const bugs = data?.data ?? [];
    return bugs.map((bug) => {
      const discoveryDate = bug.firstOccurrenceDate ?? bug.createdDate;
      return {
        bug,
        discoveryDate,
        detectionDays: diffDays(discoveryDate, bug.causeProjectReleasedDate),
      };
    });
  }, [data]);

  // 平均検知日数（母数は検知日数が算出できたバグ）
  const average = useMemo(() => {
    const valid = rows.map((r) => r.detectionDays).filter((d): d is number => d !== null);
    if (valid.length === 0) return null;
    return Math.round((valid.reduce((sum, d) => sum + d, 0) / valid.length) * 10) / 10;
  }, [rows]);

  const downloadDetectionCsv = useCallback(() => {
    if (rows.length === 0) return;
    const headers = [
      t('kpi.detail.discoveryDate'),
      t('kpi.detail.createdDate'),
      t('kpi.detail.causeProjectReleasedDate'),
      t('kpi.detail.detectionDays'),
      t('kpi.detail.issueKey'),
      t('kpi.detail.issueTitle'),
      t('kpi.detail.causeProductKey'),
      t('kpi.detail.causeProductSummary'),
      t('kpi.detail.devLeaderGroup'),
    ];
    const csvRows = rows.map(({ bug, discoveryDate, detectionDays }) => [
      escapeCsvValue(discoveryDate),
      escapeCsvValue(bug.createdDate),
      escapeCsvValue(bug.causeProjectReleasedDate),
      escapeCsvValue(detectionDays),
      escapeCsvValue(bug.issueKey),
      escapeCsvValue(bug.summary),
      escapeCsvValue(bug.causeProjectKey),
      escapeCsvValue(bug.causeProjectSummary),
      escapeCsvValue(
        bug.devLeaderGroupName != null || bug.devLeaderTeamName != null
          ? `${bug.devLeaderGroupName ?? ''} ${bug.devLeaderTeamName ?? ''}`.trim()
          : '',
      ),
    ]);
    const csv = [headers.join(','), ...csvRows.map((row) => row.join(','))].join('\n');
    downloadCsv(`bug_detection_days_${teamDisplayName}_${displayDate}.csv`, csv);
  }, [rows, teamDisplayName, t, displayDate]);

  const description =
    average !== null
      ? t('kpi.detail.bugDetectionDaysDescription', {
          team: teamDisplayName,
          date: displayDate,
          avg: average,
          num: rows.length,
        })
      : t('kpi.detail.bugDetectionDaysDescriptionEmpty', {
          team: teamDisplayName,
          date: displayDate,
        });

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="full"
      // min-w-[1100px] のテーブルが横スクロールなしで収まるよう full より広げる
      className="md:max-w-[1240px]"
      title={t('kpi.detail.bugDetectionDaysTitle')}
      description={description}
    >
      <div className="flex items-center justify-end mb-4">
        <Button
          variant="secondary"
          size="sm"
          onClick={downloadDetectionCsv}
          disabled={isLoading || rows.length === 0}
        >
          <svg className="w-4 h-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
            />
          </svg>
          {t('kpi.detail.downloadCsv')}
        </Button>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="loading-spinner" />
        </div>
      ) : rows.length === 0 ? (
        <div className="text-center py-12 text-[var(--color-text-muted)]">
          {t('kpi.detail.noItems')}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[1100px]">
            <thead>
              <tr className="border-b border-[var(--color-border)]">
                <th className="text-left py-2 px-2 font-medium text-[var(--color-text-muted)] whitespace-nowrap w-[90px]">
                  {t('kpi.detail.discoveryDate')}
                </th>
                <th className="text-left py-2 px-2 font-medium text-[var(--color-text-muted)] whitespace-nowrap w-[90px]">
                  {t('kpi.detail.createdDate')}
                </th>
                <th className="text-left py-2 px-2 font-medium text-[var(--color-text-muted)] whitespace-nowrap w-[110px]">
                  {t('kpi.detail.causeProjectReleasedDate')}
                </th>
                <th className="text-right py-2 px-2 font-medium text-[var(--color-text-muted)] whitespace-nowrap w-[90px]">
                  {t('kpi.detail.detectionDays')}
                </th>
                <th className="text-left py-2 px-2 font-medium text-[var(--color-text-muted)] whitespace-nowrap w-[140px]">
                  {t('kpi.detail.issueKey')}
                </th>
                <th className="text-left py-2 px-2 font-medium text-[var(--color-text-muted)] min-w-[180px]">
                  {t('kpi.detail.issueTitle')}
                </th>
                <th className="text-left py-2 px-2 font-medium text-[var(--color-text-muted)] whitespace-nowrap w-[140px]">
                  {t('kpi.detail.causeProductKey')}
                </th>
                <th className="text-left py-2 px-2 font-medium text-[var(--color-text-muted)] min-w-[140px]">
                  {t('kpi.detail.causeProductSummary')}
                </th>
                <th className="text-left py-2 px-2 font-medium text-[var(--color-text-muted)] whitespace-nowrap w-[100px]">
                  {t('kpi.detail.devLeaderGroup')}
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map(({ bug, discoveryDate, detectionDays }, index) => (
                <tr
                  key={`${bug.issueKey}-${String(index)}`}
                  className="border-b border-[var(--color-border)] last:border-b-0 hover:bg-[var(--color-bg-hover)]"
                >
                  <td className="py-2 px-2 text-[var(--color-text-primary)] whitespace-nowrap">
                    {discoveryDate}
                  </td>
                  <td className="py-2 px-2 text-[var(--color-text-secondary)] whitespace-nowrap">
                    {bug.createdDate}
                  </td>
                  <td className="py-2 px-2 text-[var(--color-text-primary)] whitespace-nowrap">
                    {bug.causeProjectReleasedDate ?? '-'}
                  </td>
                  <td className="py-2 px-2 text-right font-medium text-[var(--color-text-primary)] whitespace-nowrap">
                    {detectionDays !== null ? t('kpi.daysUnit', { days: detectionDays }) : '-'}
                  </td>
                  <td className="py-2 px-3 whitespace-nowrap">
                    <a
                      href={`https://air-closet.backlog.jp/view/${bug.issueKey}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[var(--color-accent-primary)] hover:underline"
                    >
                      {bug.issueKey}
                    </a>
                  </td>
                  <td className="py-2 px-3 text-[var(--color-text-primary)]">
                    <div className="line-clamp-2">{bug.summary}</div>
                  </td>
                  <td className="py-2 px-3 whitespace-nowrap">
                    {bug.causeProjectKey ? (
                      <a
                        href={`https://air-closet.backlog.jp/view/${bug.causeProjectKey}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[var(--color-accent-primary)] hover:underline"
                      >
                        {bug.causeProjectKey}
                      </a>
                    ) : (
                      <span className="text-[var(--color-text-muted)]">-</span>
                    )}
                  </td>
                  <td className="py-2 px-3 text-[var(--color-text-primary)]">
                    <div className="line-clamp-2">{bug.causeProjectSummary ?? '-'}</div>
                  </td>
                  <td className="py-2 px-3 text-[var(--color-text-secondary)] whitespace-nowrap">
                    {bug.devLeaderGroupName || bug.devLeaderTeamName
                      ? `${bug.devLeaderGroupName ?? ''} ${bug.devLeaderTeamName ?? ''}`.trim()
                      : '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Modal>
  );
}
